import { Box, Theme, makeStyles } from '@material-ui/core';
import { WbTruncatedTypographyWrapper } from '../WbTruncatedTypographyWrapper';
import clsx from 'clsx';
import { useBreakpointValue } from '../../hooks';
import { FC, isValidElement, useMemo } from 'react';
import { startCase } from 'lodash';

const useStyles = makeStyles<Theme>(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  nestedRoot: {
    paddingTop: theme.spacing(1),
  },
  rowRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  rowColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  key: {
    width: 250,
    fontWeight: 600,
    color: theme.palette.text.primary,
    textTransform: 'capitalize',
  },
  value: {
    color: theme.palette.text.secondary,
    flex: 1,
  },
}));

type Props = {
  metadata: Record<string, any>;
  depth?: number;
  customClass?: string;
};

export const WbMetadataTable: FC<Props> = ({
  metadata,
  depth = 0,
  customClass,
}) => {
  const direction: 'column' | 'row' =
    useBreakpointValue({ xs: 'column', sm: 'row' }) ?? 'row';

  const classes = useStyles();

  const dynamicStyles = useMemo(
    () => ({
      nestedKey: {
        width: 250,
        fontWeight: 500,
        color: 'inherit',
        paddingLeft: depth * 32,
        textTransform: 'capitalize' as const,
      },
      valueIndent: {
        marginLeft: direction === 'column' ? depth * 32 : 0,
      },
    }),
    [depth, direction],
  );

  const rowClass = direction === 'row' ? classes.rowRow : classes.rowColumn;

  const entries = useMemo(() => Object.entries(metadata ?? {}), [metadata]);

  if (!metadata || typeof metadata !== 'object') {
    return null;
  }

  return (
    <div className={clsx(classes.root, customClass)}>
      {entries.map(([key, value]) => {
        const keyStyle = depth > 0 ? dynamicStyles.nestedKey : undefined;

        if (isValidElement(value)) {
          return (
            <Box key={key} className={rowClass}>
              <WbTruncatedTypographyWrapper
                customClass={depth > 0 ? undefined : classes.key}
                style={keyStyle}
                value={startCase(key)}
                enableTooltip={false}
                maxLines={5}
              />
              {value}
            </Box>
          );
        }

        const isNestedObject =
          typeof value === 'object' && value !== null && !Array.isArray(value);
        const isArrayOfObjects =
          Array.isArray(value) &&
          value.some(v => typeof v === 'object' && v !== null);
        const isArrayOfPrimitives = Array.isArray(value) && !isArrayOfObjects;

        return (
          <Box key={key}>
            <Box className={rowClass}>
              <WbTruncatedTypographyWrapper
                customClass={depth > 0 ? undefined : classes.key}
                style={keyStyle}
                value={startCase(key)}
                enableTooltip={false}
                maxLines={5}
              />
              {/* Primitive value */}

              {!isNestedObject && !Array.isArray(value) && (
                <WbTruncatedTypographyWrapper
                  customClass={classes.value}
                  style={dynamicStyles.valueIndent}
                  value={value !== null ? String(value) : '-'}
                  enableTooltip={false}
                  maxLines={5}
                />
              )}
              {/* Array of primitives */}

              {isArrayOfPrimitives && (
                <WbTruncatedTypographyWrapper
                  customClass={classes.value}
                  style={dynamicStyles.valueIndent}
                  value={value.length > 0 ? value.join(', ') : '-'}
                  enableTooltip={false}
                  maxLines={5}
                />
              )}
            </Box>
            {/* Nested object */}
            {isNestedObject && (
              <WbMetadataTable
                metadata={value}
                depth={depth + 1}
                customClass={classes.nestedRoot}
              />
            )}
            {/* Array of objects */}
            {isArrayOfObjects &&
              value.map((item, index) => (
                <WbMetadataTable
                  key={`${key}-${index}`}
                  metadata={item}
                  depth={depth + 1}
                  customClass={classes.nestedRoot}
                />
              ))}
          </Box>
        );
      })}
    </div>
  );
};
